import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cancelAPIRequest, isLastTokenNull } from "../../Utils";
import { Pagination } from "../../Components/Pagination";
import ErrorStateTable from "../../Components/ErrorStateTable";
import TableSkeleton from "../../Components/TableSkeleton";
import { businessServiceOrderStatus } from "../../Constants";
import { DateFilter, filterOptions } from "../../Components/DateFilter";
import {
  requestListBusinessServiceOrder,
  requestListBusinessServiceOrderByPartner,
  useAuthState,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import Badge from "../../Components/Badge";
import { Button } from "../../Components/Button";
import { useNavigate } from "react-router-dom";

const BusinessServiceOrder = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setpage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[4]);
  const [selectedMonth, setSelectedMonth] = useState({});
  const [selectedYear, setSelectedYear] = useState({});
  const [dateFilter, setDateFilter] = useState({});

  const { user } = useAuthState();
  const dispatch = useBusinessServiceDispatch();
  const {
    request,
    businessServiceOrders,
    businessServiceOrdersLoading,
    businessServiceOrdersError,
    nextTokens,
  } = useBusinessServiceState();

  const onLoad = (updateFilter) => {
    if (request) {
      cancelAPIRequest(request);
    }
    setpage(1);
    if (user.id) {
      if (user.membership === "CONSULTANT") {
        requestListBusinessServiceOrderByPartner(dispatch, {
          limit: 10,
          page: 1,
          createdAt: updateFilter,
          partnerOwner: user.id,
        });
      } else {
        requestListBusinessServiceOrder(dispatch, {
          limit: 10,
          page: 1,
          createdAt: updateFilter,
          owner: user.id,
        });
      }
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      if (user.id) {
        if (user.membership === "CONSULTANT") {
          requestListBusinessServiceOrderByPartner(dispatch, {
            limit: 10,
            page: nextPage,
            createdAt: dateFilter,
            partnerOwner: user.id,
            nextToken: nextTokens[nextPage],
          });
        } else {
          requestListBusinessServiceOrder(dispatch, {
            limit: 10,
            page: nextPage,
            createdAt: dateFilter,
            owner: user.id,
            nextToken: nextTokens[nextPage],
          });
        }
      }
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      if (user.id) {
        if (user.membership === "CONSULTANT") {
          requestListBusinessServiceOrderByPartner(dispatch, {
            limit: 10,
            page: prevPage,
            createdAt: dateFilter,
            partnerOwner: user.id,
            nextToken: nextTokens[prevPage],
          });
        } else {
          requestListBusinessServiceOrder(dispatch, {
            limit: 10,
            page: prevPage,
            createdAt: dateFilter,
            owner: user.id,
            nextToken: nextTokens[prevPage],
          });
        }
      }
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      if (user.id) {
        if (user.membership === "CONSULTANT") {
          requestListBusinessServiceOrderByPartner(dispatch, {
            limit: 10,
            page: selectedPage,
            createdAt: dateFilter,
            partnerOwner: user.id,
            nextToken: nextTokens[selectedPage],
          });
        } else {
          requestListBusinessServiceOrder(dispatch, {
            limit: 10,
            page: selectedPage,
            createdAt: dateFilter,
            owner: user.id,
            nextToken: nextTokens[selectedPage],
          });
        }
      }
    }
  };

  useEffect(() => {
    let updatedAt = {};

    if (selectedFilter.value === "TODAY") {
      const startOfDay = dayjs().startOf("day").toISOString();
      const endOfDay = dayjs().endOf("day").toISOString();
      updatedAt = {
        between: [startOfDay, endOfDay],
      };
    } else if (selectedFilter.value === "LAST_HOUR") {
      const startOfLastHour = dayjs().subtract(1, "hour").toISOString();
      const endOfNow = dayjs().toISOString();
      updatedAt = {
        between: [startOfLastHour, endOfNow],
      };
    } else if (selectedFilter.value === "YESTERDAY") {
      const startOfYesterday = dayjs()
        .subtract(1, "day")
        .startOf("day")
        .toISOString();
      const endOfYesterday = dayjs()
        .subtract(1, "day")
        .endOf("day")
        .toISOString();
      updatedAt = {
        between: [startOfYesterday, endOfYesterday],
      };
    } else if (selectedFilter.value === "WEEK") {
      updatedAt = {
        gt: dayjs().subtract(7, "day").toISOString().substring(0, 10),
      };
    } else if (selectedFilter.value === "MONTH") {
      updatedAt = {
        gt: dayjs().subtract(1, "month").toISOString().substring(0, 10),
      };
    } else if (selectedFilter.value === "OTHER") {
      if (selectedMonth.value && selectedYear.value) {
        const actualMonthValue = parseInt(selectedMonth.value, 10);
        const firstDayOfMonth = dayjs()
          .year(selectedYear.value)
          .month(actualMonthValue - 1)
          .startOf("month")
          .toISOString();
        const lastDayOfMonth = dayjs()
          .year(selectedYear.value)
          .month(actualMonthValue - 1)
          .endOf("month")
          .toISOString();

        updatedAt = {
          between: [firstDayOfMonth, lastDayOfMonth],
        };
      }
    }

    setDateFilter(updatedAt);
    onLoad(updatedAt);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, dispatch, selectedFilter, selectedMonth, selectedYear]);

  const getStatusDetails = (status) => {
    return businessServiceOrderStatus.find((item) => item.value === status);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col flex-wrap sm:flex-row sm:items-center sm:w-full justify-between gap-4">
        <div className="flex flex-col flex-wrap sm:flex-row gap-4">
          <DateFilter
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        </div>
        <div className="flex flex-col gap-4 justify-around sm:flex-row">
          <div className="w-full justify-start sm:justify-end">
            <Button
              className="w-full"
              onClick={() => navigate("/business-license")}
              title="orderService"
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("ID")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("serviceName")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Add-On")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Status")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Consultation")}
                    </th>
                  </tr>
                </thead>
                {businessServiceOrdersLoading ? (
                  <TableSkeleton rows={3} columns={5} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {businessServiceOrders.length > 0 ? (
                      businessServiceOrders.map((order) => {
                        const businessService = order.businessService;
                        const addOns = order.addOns.items;
                        const shortId = order.id.substr(order.id.length - 5);

                        return (
                          <tr key={order.id}>
                            <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap uppercase">
                              <a href={`/business-service/order/${order.id}`}>
                                {shortId}
                              </a>
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700">
                              {businessService.name}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700">
                              <ul>
                                {addOns.map((addon) => (
                                  <li key={addon.addOn.id} className="flex">
                                    -
                                    <span className="ml-1">
                                      {t(addon.addOn.name)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {(() => {
                                const statusDetails = getStatusDetails(
                                  order.status,
                                );
                                if (statusDetails) {
                                  return (
                                    <Badge
                                      title={statusDetails.label}
                                      type={statusDetails.type}
                                    />
                                  );
                                }
                                return (
                                  <Badge
                                    title="Unknown Status"
                                    type="secondary"
                                  />
                                );
                              })()}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-blue-600">
                              <a href={`/consultation/${order.consultationID}`}>
                                {t("viewConsultation")}
                              </a>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <ErrorStateTable
                        colSpan={9}
                        error={businessServiceOrdersError}
                        emptyTitle={
                          isLastTokenNull(nextTokens)
                            ? "noBusinessServiceOrder"
                            : "Please click Next again to load the data"
                        }
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessServiceOrder;
